// src/utils/analytics.ts
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface AnalyticsConfig {
  measurementId: string;
  enabled: boolean;
}

class ChatAnalytics {
  private config: AnalyticsConfig;
  private initialized = false;

  constructor() {
    // Get analytics config from environment or URL params
    const urlParams = new URLSearchParams(window.location.search);
    this.config = {
      measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || urlParams.get('ga_id') || '',
      enabled: import.meta.env.VITE_GA_ENABLED === 'true' || urlParams.get('ga_enabled') === 'true'
    };
  }

  async initialize() {
    if (!this.config.enabled || !this.config.measurementId || this.initialized) {
      return;
    }

    try {
      // Load Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', this.config.measurementId, {
        // Isolate from parent page
        send_page_view: false,
        cookie_domain: 'none',
        cookie_flags: 'SameSite=None;Secure',
        // Custom config for iframe
        custom_map: {
          'custom_parameter_1': 'chat_widget'
        }
      });

      this.initialized = true;
      console.log('Chat Analytics initialized:', this.config.measurementId);
    } catch (error) {
      console.error('Failed to initialize Chat Analytics:', error);
    }
  }

  // Track chat events
  trackChatOpen() {
    this.trackEvent('chat_interaction', {
      action: 'open',
      widget_type: 'chat'
    });
  }

  trackChatClose() {
    this.trackEvent('chat_interaction', {
      action: 'close',
      widget_type: 'chat'
    });
  }

  trackMessageSent(messageLength: number) {
    this.trackEvent('chat_interaction', {
      action: 'message_sent',
      widget_type: 'chat',
      message_length: messageLength
    });
  }

  trackFormSubmission(formType: string) {
    this.trackEvent('form_submission', {
      form_type: formType,
      widget_type: 'chat'
    });
  }

  trackFormView(formType: string) {
    this.trackEvent('form_view', {
      form_type: formType,
      widget_type: 'chat'
    });
  }

  // Generic event tracking
  trackEvent(eventName: string, parameters: Record<string, any> = {}) {
    if (!this.initialized || !window.gtag) {
      return;
    }

    try {
      window.gtag('event', eventName, {
        ...parameters,
        // Add default parameters
        chat_widget_version: '1.0.0',
        tenant_code: new URLSearchParams(window.location.search).get('tc') || 'unknown',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }

  // Track page view (for iframe)
  trackPageView(pagePath?: string) {
    if (!this.initialized || !window.gtag) {
      return;
    }

    window.gtag('event', 'page_view', {
      page_title: 'Chat Widget',
      page_location: window.location.href,
      page_path: pagePath || window.location.pathname,
      widget_type: 'chat'
    });
  }
}

// Export singleton instance
export const chatAnalytics = new ChatAnalytics();